import { useMemo, useState } from "react";
import { Hourglass } from "lucide-react";
import {
  Caption,
  LoadingState,
  VButton,
  VDialog,
  VDialogContent,
  VField,
  VInput,
  VSelect,
  VTextarea,
} from "@/components/ui";
import type { AssetType, Deployment, Dossier } from "@/v2/shared/model";
import { assetTypeLabels } from "@/v2/shared/statusMeta";
import { formatPeriod, fullName } from "@/v2/shared/format";
import { useCurrentUser } from "@/v2/auth/useCurrentUser";
import { useAvailableAssets } from "@/v2/data/queries";
import {
  useAcceptRequest,
  usePlanDeployment,
  useReplaceAsset,
} from "@/v2/data/mutations";

const ASSET_TYPES: ReadonlyArray<AssetType> = ["msp", "mobileTree", "camcar", "camtower"];
const DEFAULT_FIRM = "Vermeer Mobiele Camerasystemen BV";

// Gedeelde asset-selectie (CB-31): typefilter + enkel assets die in de
// gekozen periode beschikbaar zijn.
function AssetPicker({
  startDate,
  endDate,
  assetId,
  onSelect,
  error,
  label,
}: {
  startDate: string;
  endDate: string;
  assetId: string;
  onSelect: (id: string) => void;
  error?: string;
  label: string;
}): React.JSX.Element {
  const [type, setType] = useState<AssetType | "all">("all");
  const available = useAvailableAssets(startDate, endDate, type === "all" ? undefined : type);
  const options = useMemo(() => available.data ?? [], [available.data]);

  return (
    <>
      <VField label="Type" hint="Filter de beschikbare assets op cameratype.">
        <div className="flex flex-wrap gap-2">
          {(["all", ...ASSET_TYPES] as const).map((t) => (
            <VButton
              key={t}
              size="sm"
              variant={type === t ? "primary" : "outline"}
              onClick={() => {
                setType(t === "all" ? "all" : t);
                onSelect("");
              }}
            >
              {t === "all" ? "Alle types" : assetTypeLabels[t]}
            </VButton>
          ))}
        </div>
      </VField>

      <VField
        label={label}
        htmlFor="asset-picker"
        hint="Enkel assets die in de gekozen periode beschikbaar zijn."
        error={error}
      >
        {available.isPending ? (
          <LoadingState label="Beschikbaarheid berekenen…" />
        ) : (
          <VSelect
            id="asset-picker"
            value={assetId}
            onChange={(e) => onSelect(e.target.value)}
          >
            <option value="">Kies een asset</option>
            {options.map((a) => (
              <option key={a.id} value={a.id}>
                {a.number} · {assetTypeLabels[a.type]}
              </option>
            ))}
          </VSelect>
        )}
        {!available.isPending && options.length === 0 ? (
          <Caption tone="muted">Geen assets beschikbaar in deze periode.</Caption>
        ) : null}
      </VField>
    </>
  );
}

// CB-12: aanvraag aanvaarden — er ontstaat een dossier (los van de aanvraag)
// met de toegekende periode. Er wordt hier nog géén asset ingepland; het
// dossier start op de wachtrij en blijft daar tot er later een inzet gepland
// wordt (zie de inzetten binnen het dossier).
export function AcceptDialog({
  open,
  onOpenChange,
  requestId,
  defaultStart,
  defaultEnd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requestId: string;
  defaultStart: string;
  defaultEnd: string;
}): React.JSX.Element {
  const user = useCurrentUser();
  const author = fullName(user.firstName, user.lastName);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [error, setError] = useState("");

  const accept = useAcceptRequest();

  function close(): void {
    setError("");
    onOpenChange(false);
  }

  return (
    <VDialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <VDialogContent
        title="Aanvraag aanvaarden"
        description="Er wordt een dossier aangemaakt met de toegekende periode. Het dossier komt op de wachtrij te staan. Een asset plan je nadien in via een inzet."
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <VField label="Toegekende begindatum" htmlFor="acc-start" required>
              <VInput
                id="acc-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </VField>
            <VField label="Toegekende einddatum" htmlFor="acc-end" required>
              <VInput
                id="acc-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </VField>
          </div>

          <div className="flex items-center gap-2 rounded-lg bg-warning-50 px-3 py-2 text-warning-800">
            <Hourglass className="size-4 shrink-0" aria-hidden />
            <Caption tone="muted" className="text-warning-800">
              Het dossier start op de wachtrij tot je een inzet met een asset plant.
            </Caption>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
            <VButton variant="ghost" onClick={close}>
              Annuleren
            </VButton>
            <VButton
              disabled={accept.isPending}
              onClick={() =>
                accept.mutate(
                  { id: requestId, startDate, endDate, author, handlerId: user.id },
                  { onSuccess: close, onError: (e) => setError(e.message) },
                )
              }
            >
              Aanvaarden
            </VButton>
          </div>
          {error ? <Caption tone="muted" className="text-error-600">{error}</Caption> : null}
        </div>
      </VDialogContent>
    </VDialog>
  );
}

// CB-16/CB-39: inzet plannen binnen een bestaand dossier.
export function PlanDeploymentDialog({
  open,
  onOpenChange,
  requestId,
  dossier,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requestId: string;
  dossier: Dossier;
}): React.JSX.Element {
  const user = useCurrentUser();
  const author = fullName(user.firstName, user.lastName);

  const [expectedStart, setExpectedStart] = useState(dossier.startDate);
  const [expectedEnd, setExpectedEnd] = useState(dossier.endDate);
  const [assetId, setAssetId] = useState("");
  const [firm, setFirm] = useState("");
  const [error, setError] = useState("");

  const plan = usePlanDeployment();

  function close(): void {
    setAssetId("");
    setError("");
    onOpenChange(false);
  }

  return (
    <VDialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <VDialogContent
        title="Inzet plannen"
        description="Koppel één asset aan dit dossier voor een verwachte plaatsingsperiode."
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <VField label="Verwachte start" htmlFor="dep-start">
              <VInput
                id="dep-start"
                type="date"
                value={expectedStart}
                onChange={(e) => setExpectedStart(e.target.value)}
              />
            </VField>
            <VField label="Verwacht einde" htmlFor="dep-end">
              <VInput
                id="dep-end"
                type="date"
                value={expectedEnd}
                onChange={(e) => setExpectedEnd(e.target.value)}
              />
            </VField>
          </div>

          <AssetPicker
            startDate={expectedStart}
            endDate={expectedEnd}
            assetId={assetId}
            onSelect={(id) => {
              setAssetId(id);
              setError("");
            }}
            error={error || undefined}
            label="Asset"
          />

          <VField label="Externe plaatsingsfirma" htmlFor="dep-firm" optional>
            <VInput
              id="dep-firm"
              placeholder={DEFAULT_FIRM}
              value={firm}
              onChange={(e) => setFirm(e.target.value)}
            />
          </VField>

          <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
            <VButton variant="ghost" onClick={close}>
              Annuleren
            </VButton>
            <VButton
              disabled={plan.isPending}
              onClick={() => {
                if (!assetId) {
                  setError("Kies een asset.");
                  return;
                }
                plan.mutate(
                  {
                    requestId,
                    dossierId: dossier.id,
                    input: {
                    assetId,
                    expectedStart,
                    expectedEnd,
                    externalFirm: firm.trim() || undefined,
                  },
                    author,
                  },
                  { onSuccess: close, onError: (e) => setError(e.message) },
                );
              }}
            >
              Inzet plannen
            </VButton>
          </div>
        </div>
      </VDialogContent>
    </VDialog>
  );
}

// CB-16: asset van een inzet vervangen met reden (ook ander type).
export function ReplaceAssetDialog({
  open,
  onOpenChange,
  requestId,
  dossierId,
  deployment,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requestId: string;
  dossierId: string;
  deployment: Deployment;
}): React.JSX.Element {
  const user = useCurrentUser();
  const author = fullName(user.firstName, user.lastName);

  const [assetId, setAssetId] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const replaceAsset = useReplaceAsset();

  function close(): void {
    setAssetId("");
    setReason("");
    setError("");
    onOpenChange(false);
  }

  return (
    <VDialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <VDialogContent
        title="Asset vervangen"
        description="Vervang het asset van deze inzet met opgave van een reden. Ook een ander type kan."
      >
        <div className="space-y-4">
          <Caption tone="muted">
            Verwachte periode: {formatPeriod(deployment.expectedStart, deployment.expectedEnd)}
          </Caption>

          <AssetPicker
            startDate={deployment.expectedStart}
            endDate={deployment.expectedEnd}
            assetId={assetId}
            onSelect={(id) => {
              setAssetId(id);
              setError("");
            }}
            error={error && !error.includes("reden") ? error : undefined}
            label="Vervangend asset"
          />

          <VField
            label="Reden van vervanging"
            htmlFor="rep-reason"
            required
            error={error.includes("reden") ? error : undefined}
          >
            <VTextarea
              id="rep-reason"
              rows={2}
              placeholder="Bv. defect, batterijprobleem, ander type nodig…"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError("");
              }}
            />
          </VField>

          <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
            <VButton variant="ghost" onClick={close}>
              Annuleren
            </VButton>
            <VButton
              disabled={replaceAsset.isPending}
              onClick={() => {
                if (!assetId) {
                  setError("Kies een vervangend asset.");
                  return;
                }
                if (!reason.trim()) {
                  setError("Geef een reden op voor de vervanging.");
                  return;
                }
                replaceAsset.mutate(
                  {
                    requestId,
                    dossierId,
                    deploymentId: deployment.id,
                    newAssetId: assetId,
                    reason,
                    author,
                  },
                  { onSuccess: close, onError: (e) => setError(e.message) },
                );
              }}
            >
              Vervangen
            </VButton>
          </div>
        </div>
      </VDialogContent>
    </VDialog>
  );
}
